import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import type { Me, Project, Connection } from '../lib/types';
import { navigate } from '../lib/router';
import { usd, dateTime, PROVIDER_NAME, RUN_STATE_TEXT } from '../lib/format';

const TABS = [
  ['connections', 'Bağlantılar'],
  ['budget', 'Bütçe'],
  ['usage', 'Kullanım'],
  ['profile', 'Çalışma profili'],
  ['mcp', 'MCP erişimi'],
  ['sessions', 'Oturumlar'],
] as const;

interface Props {
  tab: string;
  me: Me;
  projects: Project[];
  onConnectionsChanged: () => void;
  onOpenMenu: () => void;
}

export function Settings({ tab, me, projects, onConnectionsChanged, onOpenMenu }: Props) {
  const tabs: ReadonlyArray<readonly [string, string]> = me.isPlatformAdmin
    ? [...TABS, ['admin', 'Yönetim'] as const]
    : TABS;
  return (
    <>
      <header className="topbar">
        <button className="btn small only-narrow" onClick={onOpenMenu}>
          Menü
        </button>
        <h1>Ayarlar</h1>
      </header>
      <div className="tabs" role="tablist" aria-label="Ayarlar">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            className="tab"
            onClick={() => navigate(`/settings/${id}`)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="page" role="tabpanel">
        <div className="page-inner">
          {tab === 'connections' && <Connections onChanged={onConnectionsChanged} />}
          {tab === 'budget' && <Budget projects={projects} />}
          {tab === 'usage' && <Usage />}
          {tab === 'profile' && <Profile />}
          {tab === 'mcp' && <Mcp projects={projects} />}
          {tab === 'sessions' && <Sessions />}
          {tab === 'admin' && me.isPlatformAdmin && <Admin me={me} />}
        </div>
      </div>
    </>
  );
}

interface ProviderInfo {
  id: string;
  label: string;
  capabilities: {
    subscriptionInThirdPartyApp: string;
    cancelStopsBilling: string;
    quotaReadout: string;
    runtimeVerified: boolean;
  };
  models: {
    model: string;
    input: number;
    output: number;
    validFrom: string;
    validTo: string | null;
    source: string;
  }[];
}

function Connections({ onChanged }: { onChanged: () => void }) {
  const [list, setList] = useState<Connection[]>([]);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [priceVersion, setPriceVersion] = useState('');
  const [form, setForm] = useState({ provider: 'anthropic', label: '', apiKey: '', defaultModel: '' });
  const [msg, setMsg] = useState<string | null>(() => {
    const q = new URLSearchParams(location.search);
    const ok = q.get('openrouter');
    const err = q.get('openrouter_error');
    if (!ok && !err) return null;
    history.replaceState(null, '', location.pathname);
    if (ok === 'connected')
      return 'OpenRouter bağlandı. Ücretsiz @ai hemen kullanılabilir; @claude, @codex ve @gemini OpenRouter hesabınızla çalışır ve ücreti OpenRouter kredinizden düşer. Ücretli modeller, Bütçe sekmesinden aylık bir sınır koyana kadar çalışmaz.';
    return err === 'state_mismatch'
      ? 'Bağlantı süresi doldu veya başka bir oturumda başlatıldı. Tekrar deneyin.'
      : err === 'cancelled'
        ? 'OpenRouter bağlantısı iptal edildi.'
        : `OpenRouter bağlanamadı (${err}). Tekrar deneyin.`;
  });
  const [orModels, setOrModels] = useState<
    Record<string, { id: string; inputPerM: number; outputPerM: number; free?: boolean }[]>
  >({});
  const isFree = (provider: string, id: string) => !!orModels[provider]?.find((m) => m.id === id && m.free);
  const load = async () => {
    const l = await api.get<Connection[]>('/v1/me/connections');
    setList(l);
    for (const p of new Set(l.filter((c) => c.via === 'openrouter').map((c) => c.provider)))
      api
        .get<{ id: string; inputPerM: number; outputPerM: number }[]>(
          `/v1/me/connections/openrouter/models?provider=${p}`,
        )
        .then((m) => setOrModels((x) => ({ ...x, [p]: m })))
        .catch(() => undefined);
  };
  const openrouterConnected = list.some((c) => c.via === 'openrouter');
  useEffect(() => {
    void load();
    api.get<{ priceTableVersion: string; providers: ProviderInfo[] }>('/v1/providers').then((p) => {
      setProviders(p.providers);
      setPriceVersion(p.priceTableVersion);
    });
  }, []);
  const prov = providers.find((p) => p.id === form.provider);

  async function add(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await api.post('/v1/me/connections', {
        ...form,
        defaultModel: form.defaultModel || prov?.models[0]?.model,
      });
      setForm({ ...form, label: '', apiKey: '' });
      await load();
      onChanged();
      setMsg('Bağlantı kaydedildi. Anahtar şifreli saklanır ve bir daha gösterilmez.');
    } catch (err) {
      setMsg(err instanceof ApiError ? `Kaydedilemedi (${err.code}).` : 'Kaydedilemedi.');
    }
  }

  async function verify(id: string) {
    setMsg('Sağlayıcıya model listesi isteği gönderiliyor…');
    const r = await api.post<{ status: string; error?: string; defaultModelAvailable?: boolean }>(
      `/v1/me/connections/${id}/verify`,
    );
    setMsg(
      r.status === 'verified'
        ? r.defaultModelAvailable
          ? 'Doğrulandı: anahtar çalışıyor ve seçili model listede.'
          : 'Anahtar çalışıyor, ancak seçili model sağlayıcı listesinde yok.'
        : `Doğrulanamadı: ${r.error}`,
    );
    await load();
  }

  return (
    <>
      <h2>AI bağlantılarınız</h2>
      <section className="connect-card">
        <h3>OpenRouter ile tek tıkla bağlan</h3>
        <p className="hint">
          Kendi OpenRouter hesabınızda onay verin; anahtar otomatik ve şifreli kaydedilir, hiçbir şey
          kopyalamazsınız.
          <code>@ai</code> ücretsiz modellerle (kredi ve bütçe gerekmez), <code>@claude</code>,{' '}
          <code>@codex</code> ve <code>@gemini</code> ise ücretli modellerle bu bağlantı üzerinden çalışır;
          ücret <strong>sizin</strong> OpenRouter kredinizden düşer. Claude, ChatGPT ve Gemini aboneliklerini
          üçüncü taraf uygulamalarda kullanmak sağlayıcı kurallarınca izinli değildir.
        </p>
        <div style={{ display: 'flex', gap: 'var(--s3)', flexWrap: 'wrap' }}>
          <a className="btn primary" href="/v1/me/connections/openrouter/start">
            {openrouterConnected ? 'OpenRouter’ı yeniden bağla' : 'OpenRouter ile bağlan'}
          </a>
          {openrouterConnected && (
            <button
              className="btn danger"
              onClick={async () => {
                if (
                  !confirm('OpenRouter bağlantısı kaldırılsın mı? Anahtar silinir; yeni çağrılar başlamaz.')
                )
                  return;
                await api.del('/v1/me/connections/openrouter');
                await load();
                onChanged();
              }}
            >
              Bağlantıyı kaldır
            </button>
          )}
        </div>
        {openrouterConnected && (
          <p className="hint" style={{ marginTop: 'var(--s3)' }}>
            Kredi yüklemeden denemek için Gemini satırında “— ücretsiz” işaretli bir modeli seçin; ücretsiz
            modeller bütçe gerektirmez ama kullanım sınırları düşüktür. Claude ve GPT’nin ücretsiz modeli
            yoktur; onlar için OpenRouter kredisi ve Bütçe sekmesinden bir aylık sınır gerekir.
          </p>
        )}
      </section>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Sağlayıcı</th>
              <th>Ad</th>
              <th>Anahtar</th>
              <th>Model</th>
              <th>Durum</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="hint">
                  Henüz bağlantınız yok.
                </td>
              </tr>
            )}
            {list.map((c) => (
              <tr key={c.id}>
                <td>
                  {PROVIDER_NAME[c.provider] ?? c.provider}
                  {c.via === 'openrouter' && <div className="meta">OpenRouter üzerinden</div>}
                </td>
                <td>{c.label}</td>
                <td>
                  <code>{c.keyHint}</code>
                </td>
                <td>
                  <select
                    className="select"
                    aria-label={`${c.label} modeli`}
                    value={c.defaultModel}
                    onChange={async (e) => {
                      await api.patch(`/v1/me/connections/${c.id}`, { defaultModel: e.target.value });
                      await load();
                    }}
                  >
                    {[
                      c.defaultModel,
                      ...(c.via === 'openrouter'
                        ? (orModels[c.provider] ?? []).map((m) => m.id)
                        : (providers.find((p) => p.id === c.provider)?.models.map((m) => m.model) ?? [])),
                    ]
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .map((m) => (
                        <option key={m} value={m}>
                          {isFree(c.provider, m) ? `${m} — ücretsiz` : m}
                        </option>
                      ))}
                  </select>
                </td>
                <td className={`status-${c.status}`}>
                  {c.status === 'verified'
                    ? 'doğrulandı'
                    : c.status === 'failed'
                      ? `hata: ${c.lastError}`
                      : 'doğrulanmadı'}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn small" onClick={() => verify(c.id)}>
                    Doğrula
                  </button>{' '}
                  <button
                    className="btn small danger"
                    onClick={async () => {
                      if (!confirm('Bağlantı iptal edilsin mi? Anahtar silinir; yeni çağrılar başlamaz.'))
                        return;
                      await api.del(`/v1/me/connections/${c.id}`);
                      await load();
                      onChanged();
                    }}
                  >
                    İptal et
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {msg && (
        <p className="notice" role="status">
          {msg}
        </p>
      )}

      <details>
        <summary>Gelişmiş: sağlayıcı API anahtarıyla bağlan</summary>
        <form onSubmit={add}>
          <div className="form-grid">
            <label className="field">
              <span>Sağlayıcı</span>
              <select
                className="select"
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value, defaultModel: '' })}
              >
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Ad (yalnızca siz görürsünüz)</span>
              <input
                className="input"
                autoComplete="off"
                name="connection-label"
                required
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Varsayılan model</span>
              <select
                className="select"
                value={form.defaultModel}
                onChange={(e) => setForm({ ...form, defaultModel: e.target.value })}
              >
                {prov?.models.map((m) => (
                  <option key={m.model + m.validFrom} value={m.model}>
                    {m.model} (${m.input} / ${m.output} per 1M token)
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="field">
            <span>API anahtarı</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              name="provider-api-key"
              data-1p-ignore
              data-lpignore="true"
              required
              minLength={8}
              value={form.apiKey}
              onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
            />
          </label>
          <button className="btn primary" type="submit">
            Bağlantıyı kaydet
          </button>
        </form>
        {prov && (
          <p className="hint" style={{ marginTop: 'var(--s3)' }}>
            {prov.capabilities.cancelStopsBilling === 'no' &&
              'Bu sağlayıcıda durdurma, sağlayıcının ücretlendirmesini durdurmaz. '}
            {prov.capabilities.quotaReadout === 'none_documented' &&
              'Kalan kota bilgisi sağlayıcı tarafından sunulmuyor. '}
            Fiyat tablosu sürümü {priceVersion}; maliyetler token sayısından tahmin edilir, fatura değildir.
            Gerçek sağlayıcı entegrasyonu bu sürümde henüz canlı test edilmedi.
          </p>
        )}
      </details>
    </>
  );
}

interface BudgetRow {
  id: string;
  provider: string | null;
  projectId: string | null;
  softLimitMicros: number;
  hardLimitMicros: number;
}

function Budget({ projects }: { projects: Project[] }) {
  const [rows, setRows] = useState<BudgetRow[]>([]);
  const [f, setF] = useState({ provider: '', projectId: '', soft: '5', hard: '10' });
  const [err, setErr] = useState<string | null>(null);
  const load = async () => setRows(await api.get<BudgetRow[]>('/v1/me/budgets'));
  useEffect(() => void load(), []);
  return (
    <>
      <h2>Aylık bütçe</h2>
      <p className="hint">
        Üst sınır tanımlı değilse ücretli AI çağrısı başlamaz. Her çağrı başlamadan önce tahmini en yüksek
        maliyet ayrılır; sınır aşılacaksa çağrı başlatılmaz. Converoom, sağlayıcıda başka uygulamalarla
        yaptığınız harcamayı göremez.
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Kapsam</th>
              <th className="num">Uyarı sınırı</th>
              <th className="num">Üst sınır</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  {r.provider ? PROVIDER_NAME[r.provider] : 'Tüm sağlayıcılar'} —{' '}
                  {r.projectId
                    ? (projects.find((p) => p.id === r.projectId)?.name ?? 'proje')
                    : 'tüm projeler'}
                </td>
                <td className="num">{usd(r.softLimitMicros)}</td>
                <td className="num">{usd(r.hardLimitMicros)}</td>
                <td>
                  <button
                    className="btn small danger"
                    onClick={async () => {
                      await api.del(`/v1/me/budgets/${r.id}`);
                      await load();
                    }}
                  >
                    Kaldır
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <section>
        <h3>Sınır belirle</h3>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setErr(null);
            try {
              await api.put('/v1/me/budgets', {
                provider: f.provider || null,
                projectId: f.projectId || null,
                softLimitUsd: Number(f.soft),
                hardLimitUsd: Number(f.hard),
              });
              await load();
            } catch (er) {
              setErr(
                er instanceof ApiError && er.code === 'soft_above_hard'
                  ? 'Uyarı sınırı üst sınırdan büyük olamaz.'
                  : 'Kaydedilemedi.',
              );
            }
          }}
        >
          <div className="form-grid">
            <label className="field">
              <span>Sağlayıcı</span>
              <select
                className="select"
                value={f.provider}
                onChange={(e) => setF({ ...f, provider: e.target.value })}
              >
                <option value="">Tümü</option>
                {['anthropic', 'openai', 'gemini'].map((p) => (
                  <option key={p} value={p}>
                    {PROVIDER_NAME[p]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Proje</span>
              <select
                className="select"
                value={f.projectId}
                onChange={(e) => setF({ ...f, projectId: e.target.value })}
              >
                <option value="">Tümü</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Uyarı sınırı (USD/ay)</span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={f.soft}
                onChange={(e) => setF({ ...f, soft: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Üst sınır (USD/ay)</span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={f.hard}
                onChange={(e) => setF({ ...f, hard: e.target.value })}
              />
            </label>
          </div>
          {err && (
            <p className="error-text" role="alert">
              {err}
            </p>
          )}
          <button className="btn primary" type="submit">
            Kaydet
          </button>
        </form>
      </section>
    </>
  );
}

interface UsageReport {
  month: string;
  generatedAt: string;
  costBasis: string;
  providerReported: { available: boolean; reason: string };
  providerQuota: { available: boolean; reason: string };
  byGroup: {
    provider: string;
    model: string;
    projectName: string;
    runs: number;
    completed: number;
    blocked: number;
    unknown: number;
    inputTokens: number;
    outputTokens: number;
    reservedMicros: number;
    estimatedMicros: number;
    providerReportedMicros: number | null;
    finalMicros: number;
  }[];
  recent: {
    id: string;
    provider: string;
    model: string;
    state: string;
    blockReason: string | null;
    estimatedMicros: number | null;
    costSource: string | null;
    createdAt: string;
  }[];
}

function Usage() {
  const [u, setU] = useState<UsageReport | null>(null);
  useEffect(() => void api.get<UsageReport>('/v1/me/usage').then(setU), []);
  if (!u) return <p className="hint">Yükleniyor…</p>;
  return (
    <>
      <h2>Kişisel kullanım — {u.month}</h2>
      <p className="hint">
        Kaynak: Converoom kayıtları; son güncelleme {dateTime(u.generatedAt)}. Tutarlar tahminidir: token
        sayısı × sürümlü fiyat tablosu. Fatura değildir.
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Sağlayıcı / model</th>
              <th>Proje</th>
              <th className="num">Çağrı</th>
              <th className="num">Giriş / çıkış token</th>
              <th className="num">Ayrılmış</th>
              <th className="num">Tahmini</th>
              <th className="num">Sağlayıcı raporu</th>
            </tr>
          </thead>
          <tbody>
            {u.byGroup.length === 0 && (
              <tr>
                <td colSpan={7} className="hint">
                  Bu ay çağrı yok.
                </td>
              </tr>
            )}
            {u.byGroup.map((g) => (
              <tr key={g.provider + g.model + g.projectName}>
                <td>
                  {PROVIDER_NAME[g.provider] ?? g.provider} <span className="msg-model">{g.model}</span>
                </td>
                <td>{g.projectName}</td>
                <td className="num">
                  {g.runs} ({g.completed} tamam, {g.blocked} başlamadı
                  {g.unknown ? `, ${g.unknown} belirsiz` : ''})
                </td>
                <td className="num">
                  {g.inputTokens.toLocaleString('tr-TR')} / {g.outputTokens.toLocaleString('tr-TR')}
                </td>
                <td className="num">{usd(g.reservedMicros)}</td>
                <td className="num">{usd(g.estimatedMicros)}</td>
                <td className="num">
                  {g.providerReportedMicros === null ? 'yok' : usd(g.providerReportedMicros)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="notice">{u.providerReported.reason}</p>
      <p className="notice">Kalan sağlayıcı kotası: {u.providerQuota.reason}</p>
      <section>
        <h3>Son çağrılar</h3>
        <ul className="list">
          {u.recent.map((r) => (
            <li key={r.id}>
              {PROVIDER_NAME[r.provider] ?? r.provider} <span className="msg-model">{r.model}</span> —{' '}
              {RUN_STATE_TEXT[r.state] ?? r.state}
              {r.blockReason && ` (${r.blockReason})`}
              <div className="meta">
                {dateTime(r.createdAt)} — tahmini {usd(r.estimatedMicros)} — kaynak: {r.costSource ?? '—'}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

function Profile() {
  const [rows, setRows] = useState<{ id: string; key: string; value: string; scope: string }[]>([]);
  const [f, setF] = useState({ key: '', value: '', scope: 'private' });
  const load = async () => setRows(await api.get('/v1/me/profile'));
  useEffect(() => void load(), []);
  return (
    <>
      <h2>Çalışma profili</h2>
      <p className="hint">
        Yalnızca sizin girdiğiniz tercihler. “AI bağlamına ekle” işaretli olanlar sadece sizin başlattığınız
        AI çağrılarına eklenir; diğerleri hiçbir yere gönderilmez. Sağlayıcıların kendi hafızaları veya sohbet
        geçmişleriniz buraya aktarılmaz.
      </p>
      <ul className="list">
        {rows.map((r) => (
          <li key={r.id}>
            <strong>{r.key}</strong>: {r.value}
            <div className="meta">
              {r.scope === 'ai_context' ? 'AI bağlamına eklenir' : 'Özel'}{' '}
              <button
                className="btn small danger"
                onClick={async () => {
                  await api.del(`/v1/me/profile/${r.id}`);
                  await load();
                }}
              >
                Sil
              </button>
            </div>
          </li>
        ))}
      </ul>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          await api.post('/v1/me/profile', f);
          setF({ key: '', value: '', scope: 'private' });
          await load();
        }}
      >
        <div className="form-grid">
          <label className="field">
            <span>Başlık</span>
            <input
              className="input"
              required
              value={f.key}
              onChange={(e) => setF({ ...f, key: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Kapsam</span>
            <select
              className="select"
              value={f.scope}
              onChange={(e) => setF({ ...f, scope: e.target.value })}
            >
              <option value="private">Özel</option>
              <option value="ai_context">AI bağlamına ekle</option>
            </select>
          </label>
        </div>
        <label className="field">
          <span>Değer</span>
          <textarea
            className="textarea"
            rows={2}
            required
            value={f.value}
            onChange={(e) => setF({ ...f, value: e.target.value })}
          />
        </label>
        <button className="btn" type="submit">
          Ekle
        </button>
      </form>
    </>
  );
}

function Mcp({ projects }: { projects: Project[] }) {
  const [tokens, setTokens] = useState<
    { id: string; label: string; projectIds: string[]; scopes: string[]; expiresAt: string }[]
  >([]);
  const [created, setCreated] = useState<string | null>(null);
  const [sel, setSel] = useState<string[]>([]);
  const [write, setWrite] = useState(false);
  const load = async () => setTokens(await api.get('/v1/me/mcp-tokens'));
  useEffect(() => void load(), []);
  return (
    <>
      <h2>MCP erişimi</h2>
      <p className="hint">
        Kendi Claude / ChatGPT / Gemini CLI istemcinizden Converoom'a erişmek için kapsamlı bir belirteç
        oluşturun. Adres: <code>{location.origin}/v1/mcp</code>. Belirteç yalnızca seçtiğiniz projelerde,
        sizin erişebildiğiniz odalarda çalışır. MCP ile yazılan mesajlar AI çağırmaz. Ekip Sohbeti MCP'ye açık
        değildir.
      </p>
      {created && (
        <div className="notice" role="status">
          Belirteç yalnızca bir kez gösterilir; istemcinizin gizli ayarına kaydedin:
          <div className="secret-once">{created}</div>
          <button className="btn small" onClick={() => setCreated(null)}>
            Kaydettim, gizle
          </button>
        </div>
      )}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const r = await api.post<{ token: string }>('/v1/me/mcp-tokens', {
            label: 'MCP',
            projectIds: sel,
            scopes: write ? ['read', 'write_messages'] : ['read'],
            ttlDays: 30,
          });
          setCreated(r.token);
          await load();
        }}
      >
        <fieldset style={{ border: 0, padding: 0 }}>
          <legend className="hint">Projeler</legend>
          {projects.map((p) => (
            <label
              key={p.id}
              style={{ display: 'flex', gap: 'var(--s2)', alignItems: 'center', minHeight: 32 }}
            >
              <input
                type="checkbox"
                checked={sel.includes(p.id)}
                onChange={(e) => setSel(e.target.checked ? [...sel, p.id] : sel.filter((x) => x !== p.id))}
              />
              {p.name}
            </label>
          ))}
        </fieldset>
        <label style={{ display: 'flex', gap: 'var(--s2)', alignItems: 'center', minHeight: 32 }}>
          <input type="checkbox" checked={write} onChange={(e) => setWrite(e.target.checked)} /> Odalara mesaj
          yazabilsin
        </label>
        <button className="btn primary" type="submit" disabled={!sel.length}>
          Belirteç oluştur (30 gün)
        </button>
      </form>
      <ul className="list" style={{ marginTop: 'var(--s4)' }}>
        {tokens.map((t) => (
          <li key={t.id}>
            {t.label} — {t.scopes.join(', ')} — {t.projectIds.length} proje — bitiş {dateTime(t.expiresAt)}{' '}
            <button
              className="btn small danger"
              onClick={async () => {
                await api.del(`/v1/me/mcp-tokens/${t.id}`);
                await load();
              }}
            >
              İptal et
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

function Sessions() {
  const [rows, setRows] = useState<
    { id: string; client_kind: string; device_label: string | null; last_seen_at: string; current: boolean }[]
  >([]);
  const load = async () => setRows(await api.get('/v1/me/sessions'));
  useEffect(() => void load(), []);
  return (
    <>
      <h2>Oturumlar ve cihazlar</h2>
      <ul className="list">
        {rows.map((s) => (
          <li key={s.id}>
            {s.client_kind === 'web' ? 'Web' : 'Mobil'} {s.device_label ?? ''} {s.current && '(bu cihaz)'}
            <div className="meta">
              Son etkinlik {dateTime(s.last_seen_at)}{' '}
              {!s.current && (
                <button
                  className="btn small danger"
                  onClick={async () => {
                    await api.del(`/v1/me/sessions/${s.id}`);
                    await load();
                  }}
                >
                  Oturumu kapat
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  isPlatformAdmin: boolean;
  googleLinked: boolean;
  createdAt: string;
  disabledAt: string | null;
  lastSeenAt: string | null;
  projects: number;
}
interface AdminInvite {
  id: string;
  email: string;
  role: string;
  projectName: string | null;
  createdBy: string;
  expiresAt: string;
}

/** Platform administration: who may sign in (access grants) and account status. */
function Admin({ me }: { me: Me }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [invites, setInvites] = useState<AdminInvite[]>([]);
  const [email, setEmail] = useState('');
  const [days, setDays] = useState(14);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const [u, i] = await Promise.all([
      api.get<AdminUser[]>('/v1/admin/users'),
      api.get<AdminInvite[]>('/v1/admin/invites'),
    ]);
    setUsers(u);
    setInvites(i);
  };
  useEffect(() => void load(), []);

  async function grant(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    setError(null);
    try {
      await api.post('/v1/invites', { email: email.trim(), ttlHours: days * 24 });
      setMsg(`${email.trim()} artık Google ile giriş yapabilir (${days} gün içinde).`);
      setEmail('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Eklenemedi (${err.code}).` : 'Sunucuya ulaşılamadı.');
    }
  }

  async function act(url: string, method: 'post' | 'del') {
    setError(null);
    try {
      if (method === 'post') await api.post(url);
      else await api.del(url);
      await load();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'error';
      setError(
        code === 'cannot_disable_admin'
          ? 'Yöneticiler devre dışı bırakılamaz.'
          : code === 'cannot_disable_self'
            ? 'Kendinizi devre dışı bırakamazsınız.'
            : `İşlem yapılamadı (${code}).`,
      );
    }
  }

  return (
    <>
      <h2>Erişim izni ver</h2>
      <p className="lede">
        Converoom davetle çalışır. Buraya eklediğiniz e-posta adresi, Google hesabıyla giriş yaptığında hesabı
        açılır. Proje üyeliği proje ayarlarından verilir. Yönetici yetkisi yalnızca sunucu yapılandırmasındaki
        adreslerdedir.
      </p>
      <form
        className="inline-form"
        onSubmit={grant}
        style={{ gap: 'var(--s3)', flexWrap: 'wrap', alignItems: 'end' }}
      >
        <label className="field" style={{ flex: '2 1 240px' }}>
          <span>Google e-posta adresi</span>
          <input
            className="input"
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="field" style={{ flex: '0 1 120px' }}>
          <span>Geçerlilik (gün)</span>
          <input
            className="input"
            type="number"
            min={1}
            max={14}
            value={days}
            onChange={(e) => setDays(Math.min(14, Math.max(1, Number(e.target.value) || 1)))}
          />
        </label>
        <button className="btn primary" type="submit">
          İzin ver
        </button>
      </form>
      {msg && <p role="status">{msg}</p>}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}

      <h2>Bekleyen izinler</h2>
      {invites.length === 0 ? (
        <p className="meta">Bekleyen izin yok.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>E-posta</th>
                <th>Proje</th>
                <th>Veren</th>
                <th>Son tarih</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invites.map((i) => (
                <tr key={i.id}>
                  <td>{i.email}</td>
                  <td>{i.projectName ? `${i.projectName} (${i.role})` : '—'}</td>
                  <td>{i.createdBy}</td>
                  <td>{dateTime(i.expiresAt)}</td>
                  <td>
                    <button
                      className="btn small danger"
                      onClick={() => act(`/v1/admin/invites/${i.id}`, 'del')}
                    >
                      Geri al
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Kullanıcılar</h2>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Ad</th>
              <th>E-posta</th>
              <th>Giriş</th>
              <th>Rol</th>
              <th className="num">Proje</th>
              <th>Son etkinlik</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.displayName}</td>
                <td>{u.email}</td>
                <td>{u.googleLinked ? 'Google' : 'Parola'}</td>
                <td>{u.isPlatformAdmin ? 'Yönetici' : 'Kullanıcı'}</td>
                <td className="num">{u.projects}</td>
                <td>{u.lastSeenAt ? dateTime(u.lastSeenAt) : '—'}</td>
                <td>
                  {u.id === me.id || u.isPlatformAdmin ? null : u.disabledAt ? (
                    <button
                      className="btn small"
                      onClick={() => act(`/v1/admin/users/${u.id}/enable`, 'post')}
                    >
                      Etkinleştir
                    </button>
                  ) : (
                    <button
                      className="btn small danger"
                      onClick={() => act(`/v1/admin/users/${u.id}/disable`, 'post')}
                    >
                      Devre dışı bırak
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
